import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Plus, Minus, Timer as TimerIcon, Volume2, VolumeX, X } from "lucide-react";

// Louder, multi-tone bell synth (no assets required)
function useBeep() {
    const ctxRef = useRef(null);
    const ensure = () => {
        if (!ctxRef.current) {
            try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
        }
        return ctxRef.current;
    };
    // Generic tone — kept for subtle counts
    const tone = (freq = 880, dur = 0.15, vol = 0.35) => {
        const ctx = ensure();
        if (!ctx) return;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
        osc.connect(g).connect(ctx.destination);
        osc.start();
        osc.stop(ctx.currentTime + dur);
    };
    // Loud bell — two detuned oscillators + slight reverb-ish envelope
    const bell = (base = 880, dur = 0.8) => {
        const ctx = ensure();
        if (!ctx) return;
        const out = ctx.createGain();
        out.gain.setValueAtTime(0.0001, ctx.currentTime);
        out.gain.exponentialRampToValueAtTime(0.95, ctx.currentTime + 0.01);
        out.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
        out.connect(ctx.destination);
        // Fundamental + perfect 5th for a bell-ish sound, triangle waves for body
        [base, base * 1.5, base * 2].forEach((f, i) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = i === 0 ? "sine" : "triangle";
            osc.frequency.value = f;
            g.gain.value = i === 0 ? 1 : 0.35;
            osc.connect(g).connect(out);
            osc.start();
            osc.stop(ctx.currentTime + dur);
        });
    };
    return { tone, bell };
}

const PRESETS = [
    { name: "Tabata", work: 20, rest: 10, rounds: 8 },
    { name: "EMOM 10", work: 45, rest: 15, rounds: 10 },
    { name: "HIIT 30/30", work: 30, rest: 30, rounds: 10 },
    { name: "Strength 40/120", work: 40, rest: 120, rounds: 5 },
    { name: "Mobility Flow", work: 60, rest: 15, rounds: 6 },
];

export default function IntervalTimerPage() {
    const [work, setWork] = useState(30);
    const [rest, setRest] = useState(15);
    const [rounds, setRounds] = useState(8);
    const [round, setRound] = useState(1);
    const [phase, setPhase] = useState("ready"); // ready | work | rest | done
    const [remaining, setRemaining] = useState(30);
    const [running, setRunning] = useState(false);
    const [muted, setMuted] = useState(false);
    // Exercise queue — each round can have a named exercise shown up front + as "Next"
    const [queue, setQueue] = useState([]); // [{name, notes}]
    const [newEx, setNewEx] = useState("");
    const { tone, bell } = useBeep();

    useEffect(() => { if (phase === "ready") setRemaining(work); }, [work, phase]);

    useEffect(() => {
        if (!running) return;
        const id = setInterval(() => {
            setRemaining((r) => {
                // 3-2-1 countdown beeps during last 3 seconds of WORK and REST (subtle)
                if (r <= 3 && r > 1 && (phase === "work" || phase === "rest") && !muted) tone(660, 0.1, 0.25);
                if (r > 1) return r - 1;
                // phase transition
                if (phase === "work") {
                    if (round >= rounds) {
                        setPhase("done"); setRunning(false);
                        if (!muted) { bell(700, 0.6); setTimeout(() => bell(1000, 0.8), 250); setTimeout(() => bell(1400, 1.0), 550); }
                        return 0;
                    }
                    setPhase("rest");
                    if (!muted) bell(440, 0.7); // low bell = rest
                    return rest;
                } else if (phase === "rest") {
                    setPhase("work"); setRound((n) => n + 1);
                    if (!muted) bell(880, 0.8); // high bell = work
                    return work;
                } else if (phase === "ready") {
                    setPhase("work"); setRound(1);
                    if (!muted) bell(880, 0.8);
                    return work;
                }
                return 0;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [running, phase, round, rounds, rest, work, bell, tone, muted]);

    const start = () => {
        if (phase === "done") reset();
        if (phase === "ready" || remaining === 0) { setPhase("work"); setRound(1); setRemaining(work); if (!muted) bell(880, 0.8); }
        setRunning(true);
    };
    const pause = () => setRunning(false);
    const reset = () => { setRunning(false); setPhase("ready"); setRound(1); setRemaining(work); };
    const applyPreset = (p) => { setWork(p.work); setRest(p.rest); setRounds(p.rounds); reset(); };

    const phaseColor = phase === "work" ? "text-[var(--gold-bright)]" : phase === "rest" ? "text-[var(--text)]" : phase === "done" ? "text-[var(--success)]" : "text-[var(--text-muted)]";
    const phaseLabel = phase === "work" ? "WORK" : phase === "rest" ? "REST" : phase === "done" ? "DONE" : "READY";

    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");

    // Current + next exercise from the queue
    // Round index is 1-based; cycles through queue if fewer exercises than rounds
    const currentExercise = queue.length ? queue[(Math.max(1, round) - 1) % queue.length] : null;
    const nextExercise = queue.length ? queue[(Math.max(1, round)) % queue.length] : null;

    const addQueue = (e) => {
        e?.preventDefault();
        const name = newEx.trim();
        if (!name) return;
        setQueue([...queue, { name, notes: "" }]);
        setNewEx("");
    };
    const removeQueue = (i) => setQueue(queue.filter((_, ix) => ix !== i));

    return (
        <div className="p-8 space-y-6" data-testid="interval-timer-page">
            <div>
                <p className="label-tiny mb-2">/ Training tool</p>
                <h1 className="font-serif text-6xl">Interval Timer</h1>
            </div>

            {/* Presets */}
            <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                    <button key={p.name} onClick={() => applyPreset(p)} className="btn-ghost text-xs" data-testid={`preset-${p.name.toLowerCase().replace(/\s|\//g, "-")}`}>
                        {p.name} <span className="text-[var(--text-faint)] ml-2 font-mono">{p.work}/{p.rest}·{p.rounds}</span>
                    </button>
                ))}
            </div>

            {/* Display */}
            <div className={`border bg-[var(--surface)] p-8 text-center relative overflow-hidden ${phase === "work" ? "border-[var(--gold)]" : phase === "rest" ? "border-[var(--border-bright)]" : "border-[var(--border)]"}`}>
                <div className={`absolute inset-0 pointer-events-none ${phase === "work" ? "bg-stripes opacity-30" : ""}`} />
                <div className="relative">
                    <p className={`label-tiny mb-3 ${phaseColor}`} data-testid="timer-phase">{phaseLabel}</p>
                    {currentExercise && (
                        <p className="font-serif text-3xl md:text-4xl mb-2 truncate" data-testid="timer-current-exercise">
                            {currentExercise.name}
                        </p>
                    )}
                    <p className="font-display text-[10rem] md:text-[12rem] leading-none tracking-tight" data-testid="timer-display">{mm}:{ss}</p>
                    <p className="font-mono text-sm text-[var(--text-muted)] mt-4" data-testid="timer-round">ROUND {Math.min(round, rounds)} / {rounds}</p>
                    {nextExercise && (
                        <p className="font-mono text-xs text-[var(--gold-bright)] mt-3" data-testid="timer-next-exercise">
                            NEXT → <span className="text-[var(--text)]">{nextExercise.name}</span>
                        </p>
                    )}
                </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-3">
                <button onClick={reset} className="btn-ghost inline-flex items-center gap-2" data-testid="timer-reset"><RotateCcw size={16} /> Reset</button>
                {running ? (
                    <button onClick={pause} className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-3" data-testid="timer-pause"><Pause size={18} /> Pause</button>
                ) : (
                    <button onClick={start} className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-3" data-testid="timer-start"><Play size={18} /> Start</button>
                )}
                <button onClick={() => setMuted((m) => !m)} className="btn-ghost inline-flex items-center gap-2" data-testid="timer-mute">
                    {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                </button>
            </div>

            {/* Exercise queue */}
            <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="timer-queue-card">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <p className="label-tiny mb-1">/ Exercise queue</p>
                        <p className="text-xs text-[var(--text-muted)]">Add the moves you're cycling — each round shows the current one big, plus "next" in gold.</p>
                    </div>
                </div>
                <form onSubmit={addQueue} className="flex gap-2 mb-3">
                    <input className="input-dark flex-1 text-sm" value={newEx} onChange={(e) => setNewEx(e.target.value)} placeholder="e.g. Push-Up · Squat · Plank" data-testid="timer-queue-input" />
                    <button type="submit" className="btn-primary text-xs inline-flex items-center gap-1" data-testid="timer-queue-add"><Plus size={12} /> Add</button>
                </form>
                {queue.length === 0 ? (
                    <p className="text-xs text-[var(--text-muted)] italic">No exercises queued yet — that's fine, the timer still runs.</p>
                ) : (
                    <ol className="space-y-1 text-sm">
                        {queue.map((e, i) => {
                            const isCurrent = (Math.max(1, round) - 1) % queue.length === i && phase === "work";
                            return (
                                <li key={i} className={`flex items-center justify-between px-3 py-2 border ${isCurrent ? "border-[var(--gold)] bg-[var(--surface-hover)]" : "border-[var(--border)]"}`} data-testid={`timer-queue-item-${i}`}>
                                    <span className="font-mono text-xs text-[var(--text-muted)] w-6">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="flex-1 ml-2 truncate">{e.name}</span>
                                    {isCurrent && <span className="text-[10px] font-mono text-[var(--gold-bright)] uppercase tracking-widest mr-2">Now</span>}
                                    <button onClick={() => removeQueue(i)} className="text-[var(--text-muted)] hover:text-[var(--danger)]"><X size={12} /></button>
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>

            {/* Config */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)]">
                {[
                    { k: "work", label: "WORK (s)", value: work, set: setWork, step: 5, testid: "cfg-work" },
                    { k: "rest", label: "REST (s)", value: rest, set: setRest, step: 5, testid: "cfg-rest" },
                    { k: "rounds", label: "ROUNDS", value: rounds, set: setRounds, step: 1, testid: "cfg-rounds" },
                ].map((cfg) => (
                    <div key={cfg.k} className="bg-[var(--surface)] p-5 flex items-center justify-between">
                        <p className="label-tiny">{cfg.label}</p>
                        <div className="flex items-center gap-2">
                            <button onClick={() => cfg.set(Math.max(1, cfg.value - cfg.step))} className="btn-ghost p-2"><Minus size={14} /></button>
                            <p className="font-display text-4xl w-16 text-center" data-testid={cfg.testid}>{cfg.value}</p>
                            <button onClick={() => cfg.set(cfg.value + cfg.step)} className="btn-ghost p-2"><Plus size={14} /></button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="border border-[var(--gold-deep)] bg-[var(--surface)] p-6 relative overflow-hidden" data-testid="timer-howto">
                <div className="absolute inset-0 bg-stripes opacity-15 pointer-events-none" />
                <div className="relative space-y-4">
                    <div className="flex items-center gap-3">
                        <TimerIcon size={18} className="text-[var(--gold-bright)]" />
                        <h3 className="font-serif text-3xl">How this bell works</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2 text-sm text-[var(--text-muted)]">
                        <div><p className="label-tiny mb-2 text-[var(--gold-bright)]">/ High bell</p>Work interval starting — go hard.</div>
                        <div><p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Low bell</p>Rest interval — catch your breath.</div>
                        <div><p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Subtle ticks</p>3, 2, 1 countdown inside each phase so you know the transition is coming.</div>
                        <div><p className="label-tiny mb-2 text-[var(--gold-bright)]">/ Triple bell</p>You're done. Walk it off — strong work.</div>
                    </div>
                </div>
            </div>
        </div>
    );
}
